/**
 * translate_error - Translate backend error messages to user-friendly Spanish
 *
 * Maps raw backend/network error strings to messages suitable for display in the UI.
 * Used by useApiClient (automatic translation) and individual hooks that handle errors directly.
 *
 * @module utils/translate_error
 */

/**
 * Error patterns matched with .includes() — order matters (first match wins).
 * Keep more specific patterns BEFORE more generic ones.
 */
const SUBSTRING_PATTERNS = [
  // ── Validation ──
  ['Validation failed', 'Revisa que todos los campos estén completos y en el formato correcto.'],
  ['validation error', 'Revisa que todos los campos estén completos y en el formato correcto.'],

  // ── Auth ──
  ['Unauthorized', 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'],
  ['JWT expired', 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'],
  ['Invalid token', 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'],
  ['Invalid login credentials', 'Correo o contraseña incorrectos.'],
  ['Email not confirmed', 'Debes confirmar tu correo electrónico antes de iniciar sesión.'],
  ['User already registered', 'Este correo ya está registrado. Intenta iniciar sesión.'],
  ['Forbidden', 'No tienes permiso para realizar esta acción.'],
  ['Access denied', 'No tienes permiso para realizar esta acción.'],
  ['Authentication required', 'Debes iniciar sesión para realizar esta acción.'],
  ['User authentication required', 'Debes iniciar sesión para realizar esta acción.'],

  // ── Domain-specific (BEFORE generic patterns to avoid premature matches) ──

  // Cart
  ['cart is empty', 'Tu carrito está vacío.'],
  ['not available', 'Uno o más productos no están disponibles.'],

  // Orders (backend custom)
  ['can only view your own', 'Solo puedes ver tus propias órdenes.'],
  ['can only cancel your own', 'Solo puedes cancelar tus propias órdenes.'],
  ['not the buyer', 'No tienes permiso para esta orden.'],
  ['not the seller', 'No tienes permiso para esta orden.'],
  ['not a seller', 'No tienes permiso para esta orden.'],
  ['not a party', 'No tienes permiso para esta orden.'],
  ['tracking_number or tracking_photo_url is required', 'Debes ingresar un número de seguimiento o una foto del comprobante.'],

  // Payments
  ['does not belong to you', 'Esta orden no te pertenece.'],
  ['cannot be refunded', 'Este pago no puede ser reembolsado en su estado actual.'],
  ['cannot be cancelled', 'Este pago no puede ser cancelado en su estado actual.'],
  ['Failed to create payment preference', 'No se pudo crear la preferencia de pago. Intenta nuevamente.'],

  // Shipping
  ['Destination county code is required', 'Debes seleccionar una comuna de destino.'],
  ['Failed to calculate shipping cost', 'No se pudo calcular el costo de envío. Intenta nuevamente.'],

  // Images
  ['No files uploaded', 'No se seleccionaron archivos para subir.'],
  ['All uploads failed', 'Todas las imágenes fallaron al subir. Intenta nuevamente.'],
  ['can only delete your own', 'Solo puedes eliminar tus propias imágenes.'],

  // Reviews
  ['not delivered yet', 'La orden aún no ha sido entregada.'],
  ['Review already exists', 'Ya dejaste una reseña para esta orden.'],

  // Artist/Profile (specific 'not found' BEFORE generic)
  ['Artist profile not found', 'Perfil de artista no encontrado.'],
  ['Bank info not configured', 'Debes configurar tu información bancaria primero.'],
  ['Only artists can access', 'Solo artistas pueden acceder a esta sección.'],

  // ── Generic patterns (AFTER domain-specific) ──

  // Not found
  ['not found', 'El recurso solicitado no fue encontrado.'],
  ['Not Found', 'El recurso solicitado no fue encontrado.'],

  // Server errors
  ['Internal server error', 'Ocurrió un error en el servidor. Intenta nuevamente en unos momentos.'],
  ['Internal Server Error', 'Ocurrió un error en el servidor. Intenta nuevamente en unos momentos.'],

  // Rate limit (backend + Supabase)
  ['Too many requests', 'Demasiadas solicitudes. Espera un momento antes de intentar nuevamente.'],
  ['Too many API requests', 'Demasiadas solicitudes. Espera un momento antes de intentar nuevamente.'],
  ['rate limit', 'Demasiadas solicitudes. Espera un momento antes de intentar nuevamente.'],

  // Network (from apiClient — substring match)
  ['Network error', 'Error de conexión. Verifica tu conexión a internet.'],
  ['Request timeout', 'La solicitud tardó demasiado. Intenta nuevamente.'],

  // Conflict / duplicate
  ['already exists', 'Este recurso ya existe.'],
  ['duplicate', 'Este registro ya existe.'],

  // Supabase-specific
  ['row-level security', 'No tienes permiso para realizar esta acción.'],
  ['violates row-level security', 'No tienes permiso para realizar esta acción.'],
  ['violates unique constraint', 'Este registro ya existe.'],
  ['violates foreign key constraint', 'La operación hace referencia a un dato que no existe.'],
  ['violates check constraint', 'Revisa que todos los campos estén completos y en el formato correcto.'],
  ['JSON object requested, multiple', 'Error al procesar los datos. Intenta nuevamente.'],
  ['Unexpected token', 'Error de conexión con el servidor. Intenta nuevamente.'],
];

/**
 * Exact-match patterns for browser-level errors that should NOT be matched as substrings.
 * 'Failed to fetch' is the literal TypeError message from the browser when a network request fails.
 * We must NOT match it as a substring of e.g. 'Failed to fetch comments' (a custom error).
 */
const EXACT_PATTERNS = new Map([
  ['Failed to fetch', 'Error de conexión. Verifica tu conexión a internet.'],
  ['Load failed', 'Error de conexión. Verifica tu conexión a internet.'],
]);

/**
 * Translate a backend error message to a user-friendly Spanish message.
 *
 * @param {string} error_message - Raw error from backend or browser
 * @returns {string} User-friendly error message in Spanish
 */
export function translate_error(error_message) {
  if (!error_message || typeof error_message !== 'string') {
    return 'Ocurrió un error inesperado. Intenta nuevamente.';
  }

  // 1. Exact matches first (browser network errors)
  const exact = EXACT_PATTERNS.get(error_message);
  if (exact) return exact;

  // 2. Substring patterns (first match wins)
  for (const [pattern, translation] of SUBSTRING_PATTERNS) {
    if (error_message.includes(pattern)) {
      return translation;
    }
  }

  // 3. HTTP status code pattern: "HTTP NNN: ..."
  const http_match = error_message.match(/^HTTP (\d{3}):/);
  if (http_match) {
    const status = parseInt(http_match[1], 10);
    if (status >= 500) return 'Ocurrió un error en el servidor. Intenta nuevamente en unos momentos.';
    if (status === 404) return 'El recurso solicitado no fue encontrado.';
    if (status === 401 || status === 403) return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
    if (status === 409) return 'Conflicto con el estado actual. Recarga la página e intenta nuevamente.';
    if (status === 422) return 'Revisa que todos los campos estén completos y en el formato correcto.';
    if (status === 429) return 'Demasiadas solicitudes. Espera un momento antes de intentar nuevamente.';
    return 'Ocurrió un error inesperado. Intenta nuevamente.';
  }

  // 4. Already in Spanish — pass through
  if (/[áéíóúñ¿¡]/.test(error_message) || /^Error (al|de|del|en) /.test(error_message)) {
    return error_message;
  }

  // 5. Unknown technical message — generic fallback
  return 'Ocurrió un error inesperado. Intenta nuevamente.';
}
